import { ChangeDetectorRef, Component, OnInit, ViewChild, Input, OnChanges, SimpleChanges } from '@angular/core';
import { } from 'googlemaps';

const DEFAULT_MAP_CENTER = { lat: 12.911968, lng: 77.647221 };

@Component({
  selector: 'captain-google-map',
  template: require('./captain-google-map.component.html')
})
export class CaptainGoogleMapComponent implements OnInit, OnChanges {
  private polyline: google.maps.Polyline;
  @ViewChild('gmap') gmapElement: any;
  map: google.maps.Map;

  captainMarkerIcon = require('../../assets/captain-marker.png');
  dropMarkerIcon = require('../../assets/drop-marker.png');
  bound = new google.maps.LatLngBounds();

  captainMarker = new google.maps.Marker();
  dropMarker = new google.maps.Marker();
  dropLabelMarker = new google.maps.Marker();

  @Input('data') data: any;

  private readonly mapStyles = [{
    'elementType': 'geometry', 'stylers': [{ 'color': '#f5f5f5' }]
  }, {
    'elementType': 'labels.icon', 'stylers': [{ 'visibility': 'off' }]
  }, {
    'featureType': 'landscape', 'elementType': 'geometry.fill',
    'stylers': [{ 'color': '#edeef0' }, { 'visibility': 'on' }]
  }, {
    'featureType': 'road', 'elementType': 'geometry', 'stylers': [{ 'color': '#ffffff' }]
  }, {
    'featureType': 'road.highway', 'elementType': 'geometry.fill',
    'stylers': [{ 'color': '#fff9c4' }, { 'visibility': 'on' }]
  }, {
    'featureType': 'water', 'elementType': 'geometry.fill', 'stylers': [{ 'color': '#b5dfff' }]
  }, {
    'featureType': 'poi', 'elementType': 'geometry', 'stylers': [{ 'color': '#eeeeee' }]
  }];

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    this.initMap();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.data || !changes.data.currentValue || !this.map) return;
    this.applyData(changes.data.currentValue);
  }

  initMap() {
    this.map = new google.maps.Map(this.gmapElement.nativeElement, {
      center: new google.maps.LatLng(DEFAULT_MAP_CENTER.lat, DEFAULT_MAP_CENTER.lng),
      zoom: 15,
      streetViewControl: false,
      fullscreenControl: false,
      mapTypeControl: false,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      clickableIcons: false,
      zoomControl: false
    });
    this.map.setOptions({ styles: this.mapStyles as any });
    this.captainMarker.setMap(this.map);
    this.captainMarker.setZIndex(99999);
    this.captainMarker.setIcon({ url: this.captainMarkerIcon, scaledSize: new google.maps.Size(40, 40) });
    this.dropMarker.setMap(this.map);
    this.dropMarker.setIcon({ url: this.dropMarkerIcon, scaledSize: new google.maps.Size(36, 44) });
    this.dropLabelMarker.setZIndex(99998);

    // ngOnChanges often runs before the map exists; replay the first payload here
    if (this.data) {
      this.applyData(this.data);
    }
  }

  private applyData(data: any) {
    if (!data || !this.map) return;

    if (data.polyline) {
      const path = google.maps.geometry.encoding.decodePath(data.polyline);
      if (this.polyline) this.polyline.setMap(null);
      this.polyline = new google.maps.Polyline({
        path, geodesic: true, strokeColor: '#000000', strokeOpacity: 1.0, strokeWeight: 3
      });
      this.polyline.setMap(this.map);
      this.cdr.detectChanges();
    } else if (this.polyline) {
      this.polyline.setMap(null);
    }

    if (data.dropLocation) {
      const drop = { lat: data.dropLocation.lat, lng: data.dropLocation.lng };
      this.dropMarker.setPosition(drop);
      this.dropMarker.setMap(this.map);
      this.dropLabelMarker.setPosition(drop);
      const label = data.status === 'dropped'
        ? 'Trip completed'
        : (data.riderLocation && data.riderLocation.eta ? `Drop by ${this.etaToTime(data.riderLocation.eta)}` : null);
      if (label) {
        this.dropLabelMarker.setIcon(this.createLabelIcon(label));
        this.dropLabelMarker.setMap(this.map);
      } else {
        this.dropLabelMarker.setMap(null);
      }
    } else {
      this.dropMarker.setMap(null);
      this.dropLabelMarker.setMap(null);
    }

    if (data.riderLocation) {
      const pos = { lat: data.riderLocation.lat, lng: data.riderLocation.lng };
      this.captainMarker.setPosition(pos);
    }

    if (data.status === 'dropped') {
      this.captainMarker.setMap(null);
      if (this.polyline) this.polyline.setMap(null);
    }

    if (data.setMapCenter) {
      this.fitToRide(data);
    }
  }

  fitToRide(data: any) {
    this.bound = new google.maps.LatLngBounds();
    if (data.riderLocation) {
      this.bound.extend({ lat: data.riderLocation.lat, lng: data.riderLocation.lng });
    }
    if (data.dropLocation) {
      this.bound.extend({ lat: data.dropLocation.lat, lng: data.dropLocation.lng });
    }
    this.recenterMap();
  }

  recenterMap() {
    if (this.bound.isEmpty()) return;
    this.map.setCenter(this.bound.getCenter());
    this.map.fitBounds(this.bound, 80);
  }

  private createLabelIcon(label: string): google.maps.Icon {
    const w = label.length * 8 + 24;
    const pillH = 32;
    const tipH = 8;
    const totalH = pillH + tipH;
    const cx = w / 2;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${totalH}">
      <rect width="${w}" height="${pillH}" rx="16" fill="%23058743"/>
      <polygon points="${cx - 6},${pillH} ${cx + 6},${pillH} ${cx},${totalH}" fill="%23058743"/>
      <text x="${cx}" y="21" text-anchor="middle" fill="white"
        font-family="Arial,sans-serif" font-size="13" font-weight="600">${label}</text>
    </svg>`;
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + svg,
      scaledSize: new google.maps.Size(w, totalH),
      anchor: new google.maps.Point(cx, totalH + 44)
    };
  }

  private etaToTime(etaMinutes: number): string {
    const d = new Date(Date.now() + etaMinutes * 60000);
    return d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
  }
}
